// Third-party
import React, { Component } from 'react';
import { withRouter, RouteComponentProps, Route, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';

// Components
import { SecondaryNavigation } from '../../components/navigation';
import { Header, OverlappingBackground } from '../../components/layout';
import SettingsContact from './SettingsContact';
import SettingsBilling from './SettingsBilling';
import { ErrorBoundary } from '../../components/errors';
import SettingsPreferences from './SettingsPreferences';
import SettingsDelegates from './SettingsDelegates';
import SettingsSecurity from './SettingsSecurity';

// Utils
import { FormattedMessage, formatMessage } from '../../../utils/i18n';
import ChargebeeUtils, { SUBSCRIPTION_PLAN } from '../../../utils/3rd-party/chargebee';
import ZendeskUtils from '../../../utils/3rd-party/zendesk';
import notification from '../../../utils/notification';
import { userHasPermission, PERMISSIONS, userHasPermissionEnabled } from '../../../utils/permissions';
import { getInitialUserPreferences } from '@/utils/users';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { getEnterpriseAdminInfo } from '../../../redux/modules/enterprise';
import { setPortfolioEnterprise } from '../../../redux/modules/edit-portfolio';
import { updateMe, updatePassword, updatePreferences } from '../../../redux/modules/auth';
import { getSubscriptionInfos, getCheckoutHostedPage, getChargebeePortalSession } from '../../../redux/modules/subscription';
import { setLocale } from '../../../redux/modules/intl';
import { selectDelegateMode } from '../../../redux/modules/auth/selectors';
import User from '@/types/user';
import EnterpriseDetails from '@/types/enterprise-detail-info';
import EnterpriseAdminDetails from '@/types/enterprise-admin-details';
import Locale from '@/types/locale';
import Subscription from '@/types/subscription';
import EditableUserInfos from '@/types/editable-user-infos';
import AppRegion from '@/types/app-region';
import UserPreferencesComparisonSettings from '@/types/user-preferences-comparison-settings';
import UserPreferences from '@/types/user-preferences';

enum SettingsTabs {
  CONTACT = '/settings/contact',
  PREFERENCES = '/settings/preferences',
  SECURITY = '/settings/security',
  BILLING = '/settings/billing',
  ENTERPRISE = '/settings/enterprise',
  GROUPS = '/settings/groups',
  DELEGATES = '/settings/delegates',
}

type StateProps = {
  user: User;
  locale: Locale;
  resetPortfolio: Function;
  subscription?: Subscription;
  delegateMode: boolean;
  loading: {
    subscription: boolean;
  };
  comparisonSettings?: UserPreferencesComparisonSettings;
};

type DispatchProps = {
  updateMe: (infos: EditableUserInfos) => Promise<User>;
  updatePassword: (current: string, password: string, confirm: string) => Promise<void>;
  getChargebeePortalSession: () => Promise<any>;
  getCheckoutHostedPage: (plan: string) => Promise<any>;
  setLocale: (locale: Locale) => void;
  getEnterpriseAdminInfo: (id: string) => Promise<EnterpriseAdminDetails>;
  setPortfolioEnterprise: () => void;
  getSubscriptionInfos: () => Promise<Subscription>;
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
};

type Props = RouteComponentProps & StateProps & DispatchProps;
const Wrapper = ({ children }: { children: React.ReactNode }) => <div className="content">{children}</div>;
class LegacySettings extends Component<Props> {
  componentWillMount() {
    this.props.getSubscriptionInfos();
  }

  isUserEnterpriseAdmin = (): EnterpriseDetails | undefined => {
    const { user } = this.props;

    if (
      user.enterprise != null &&
      userHasPermissionEnabled(
        user,
        PERMISSIONS.ENTERPRISE_ADMIN,
        PERMISSIONS.ENTERPRISE_ADMIN_GROUPS,
        PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MODELS,
        PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS,
      )
    ) {
      return user.enterprise;
    }

    return undefined;
  };

  setComparisonSettings = () => {
    const { user, comparisonSettings } = this.props;
    return { ...getInitialUserPreferences(user), ...comparisonSettings };
  };

  getSettingsRoutes = () => {
    const { user, match, subscription, delegateMode } = this.props;
    const appRegion: AppRegion = user != null ? user.appConfig.appRegion : (AppRegion.CAN as AppRegion);

    return [
      {
        value: SettingsTabs.CONTACT,
        label: formatMessage('Settings.Nav.Contact'),
        hidden: !userHasPermission(user, PERMISSIONS.DELEGATE_CONTACT_SETTINGS) && delegateMode,
        path: `${match.path}/contact`,
        render: (props: RouteComponentProps) => (
          <Wrapper>
            <SettingsContact {...props} user={user} onSave={this.onSaveContactInfos} />
          </Wrapper>
        ),
      },
      {
        value: SettingsTabs.PREFERENCES,
        label: formatMessage('Settings.Nav.Preferences'),
        path: `${match.path}/preferences`,
        render: (props: RouteComponentProps) => (
          <Wrapper>
            <SettingsPreferences
              {...props}
              user={user}
              locale={this.props.locale}
              onLocaleChange={this.onLocaleChange}
              delegateMode={delegateMode}
              comparisonSettings={this.setComparisonSettings()}
              onComparisonSettingsChange={this.onComparisonSettingsChange}
            />
          </Wrapper>
        ),
      },
      {
        value: SettingsTabs.SECURITY,
        label: formatMessage('Settings.Nav.Security'),
        // Hidden for SSO users
        hidden: this.props.user.nameId != null,
        path: `${match.path}/security`,
        render: (props: RouteComponentProps) => <SettingsSecurity {...props} user={user} onUpdatePassword={this.onUpdatePassword} />,
      },
      {
        value: SettingsTabs.BILLING,
        label: formatMessage('Settings.Nav.Billing'),
        hidden: delegateMode || appRegion !== AppRegion.CAN || !!user.enterprise || !subscription,
        path: `${match.path}/billing`,
        render: (props: RouteComponentProps) => (
          <Wrapper>
            <SettingsBilling
              {...props}
              loading={this.props.loading.subscription}
              onUpgradeToPro={this.onUpgradeToPro}
              onCancelSubscription={this.onCancelSubscription}
              onOpenCustomerPortal={this.onOpenCustomerPortal}
              subscription={subscription}
            />
          </Wrapper>
        ),
      },
      {
        value: SettingsTabs.DELEGATES,
        label: formatMessage('Settings.Nav.Delegates'),
        hidden: !userHasPermission(this.props.user, PERMISSIONS.DELEGATE_MANAGEMENT) || delegateMode,
        path: `${match.path}/delegates`,
        render: (props: RouteComponentProps) => (
          <Wrapper>
            <SettingsDelegates user={user} {...props} />
          </Wrapper>
        ),
      },
    ].filter(route => !route.hidden);
  };

  onUpdatePassword = (current: string, password: string, confirm: string) => this.props.updatePassword(current, password, confirm);

  onOpenCustomerPortal = () => {
    this.props.getChargebeePortalSession().then(async (session: any) => {
      ChargebeeUtils.openPortal(session);
    });
  };

  onUpgradeToPro = () => {
    ChargebeeUtils.getInstance().openCheckout({
      hostedPage: () => this.props.getCheckoutHostedPage(SUBSCRIPTION_PLAN.PRO_MONTHLY),
    });
  };

  onCancelSubscription = () => {
    const { user } = this.props;

    this.props.getChargebeePortalSession().then(async (session: any) => {
      ChargebeeUtils.openPortal(session, {
        sectionType: ChargebeeUtils.getPortalSections().SUBSCRIPTION_CANCELLATION,
        params: {
          subscriptionId: user.chargeBeeId,
        },
      });
    });
  };

  onSaveContactInfos = (infos: EditableUserInfos) => this.props.updateMe(infos);

  componentDidMount() {
    const userEnterprise = this.isUserEnterpriseAdmin();

    if (userEnterprise) {
      this.props.setPortfolioEnterprise();
      this.props.getEnterpriseAdminInfo(userEnterprise._id);
    }
  }

  onLocaleChange = async (locale: Locale) => {
    if (locale === this.props.locale) {
      return;
    }

    this.props.setLocale(locale);
    await this.props.updatePreferences({ locale });
    ZendeskUtils.setLocale(locale);

    // Use setTimeout to make sure language has been changed before sending the notification.
    setTimeout(() => {
      notification.success(formatMessage('Settings.Preferences.LanguageChanged'));
    }, 0);
  };

  onComparisonSettingsChange = async (option: { [key: string]: string }) => {
    await this.props.updatePreferences({ comparisonSettings: { ...option } });

    // Use setTimeout to make sure settings have been changed before sending the notification.
    setTimeout(() => {
      notification.success(formatMessage('Settings.Preferences.SettingChanged'));
    }, 0);
  };

  render() {
    const { match } = this.props;

    const routes = this.getSettingsRoutes();

    return (
      <>
        <Header />
        <OverlappingBackground />
        <main id="content" className="settings">
          <div className="settings-navigation d-flex align-items-center">
            <h1 className="h3 title">
              <FormattedMessage id="Settings.PageTitle" />
            </h1>
            <SecondaryNavigation className="flex-grow-1" items={routes} />
          </div>

          <ErrorBoundary>
            {routes.map(route => (
              <Route key={route.value} path={route.path} render={route.render} />
            ))}
            <Route exact path={`${match.path}/`} render={() => <Redirect to={routes[0].path} />} />
          </ErrorBoundary>
        </main>
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  locale: state.intl.locale,
  subscription: state.subscription.subscription,
  delegateMode: selectDelegateMode(state),
  loading: {
    subscription: state.subscription.loading,
  },
  comparisonSettings: state.auth.user?.preferences?.comparisonSettings,
});

export default withRouter(
  connect(mapStateToProps, {
    updateMe,
    updatePassword,
    getChargebeePortalSession,
    getEnterpriseAdminInfo,
    setLocale,
    setPortfolioEnterprise,
    getCheckoutHostedPage,
    getSubscriptionInfos,
    updatePreferences,
  })(LegacySettings),
);
